import { Button } from "Components/Button";
import Input from "Components/Form/Input";
import { Field, FieldArray, Formik, FormikErrors } from "formik";
import * as Yup from "yup";
import TrashIcon from "Assets/Images/trashIcon.png";
import { Box } from "styles/globalComponents";
import PanelContent from "Components/Panels/PanelContent";
import SelectSearch from "Components/Form/SelectSearch";
import ExamsListIncluded from "Components/Product/ExamsListIncluded";
import { useQuery } from "react-query";
import { partnersCategoriesAllowed } from "Requests/Categories";
import SwitchInput from "Components/Switch/Input";
import { CustomCheckbox } from "Components/Form/CustomCheckbox";
import ImportDocument from "Assets/Images/import.svg";

import { useSelectUnits } from "Hooks/useSelectUnits";
import { CheckboxList } from "Components/CheckboxList";
import { SubListCheckbox } from "Components/SubListCheckbox";
import { useRegionsClientDomicile } from "Hooks/useRegionsClientDomicile";
import {
  CampFile,
  CheckboxSublist,
  PartnerFormStyle,
  ProductsListStyles,
} from "../styles";

export type PartnerFormValues = {
  name: string;
  appointmentType: { appointment: string; name: string }[];
  integration_codes: { id: string; name: string }[];
  auth_client_id: string;
  login: string;
  product_needs_authorization: boolean;
  free_visit: boolean;
  attach: File | null | string;
  pre_flow: boolean;
  categories:
    | {
        name: string;
        id: number;
      }[]
    | [];
  prod_include: { sku: string; product: string; id: number }[] | [];
  prod_exclude: { sku: string; product: string; id: number }[] | [];
  units: {
    include: number[];
    exclude: number[];
  };
};

export interface PartnerFormProps {
  onSubmit: any;
  initialValues?: PartnerFormValues;
}

const formSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .min(1, "Nome não pode estar vazio"),
  auth_client_id: Yup.string(),
  login: Yup.string(),
  appointmentType: Yup.array().of(
    Yup.object().shape({
      appointment: Yup.string(),
      name: Yup.string(),
    })
  ),
  attach: Yup.mixed(),
  integration_codes: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().optional(),
      name: Yup.string().optional(),
    })
  ),
  categories: Yup.array().of(
    Yup.object().shape({
      category: Yup.string(),
      id: Yup.string(),
    })
  ),
  prod_exclude: Yup.array().of(
    Yup.object().shape({
      product: Yup.string(),
      id: Yup.string(),
    })
  ),
  prod_include: Yup.array().of(
    Yup.object().shape({
      product: Yup.string(),
      id: Yup.string(),
    })
  ),
  units: Yup.object({
    include: Yup.array().min(1, "Selecione ao menos uma unidade"),
  }),
});

export const PartnerForm = ({ onSubmit, initialValues }: PartnerFormProps) => {
  const initialValuesForm = initialValues || {
    name: "",
    appointmentType: [{ appointment: "", name: "" }],
    integration_codes: [{ id: "", name: "" }],
    auth_client_id: "",
    login: "",
    categories: [],
    prod_include: [],
    prod_exclude: [],
    free_visit: false,
    pre_flow: false,
    product_needs_authorization: false,
    attach: null,
    units: {
      include: [],
      exclude: [],
    },
  };

  const unitsId: number[] = [];

  const { data, isLoading } = useQuery({
    queryKey: ["partner-allowed-categories"],
    queryFn: partnersCategoriesAllowed,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { regions } = useRegionsClientDomicile();

  const { selectAllUnits, selectRegion, selectUnit } = useSelectUnits();

  const { prod_categories } = data ?? {};

  const types = prod_categories?.map((tip) => ({
    name: tip.category,
    id: tip.id,
  }));

  regions?.forEach((region) =>
    region.units.forEach((unit) => unitsId.push(unit.id))
  );

  const avaliableCategoriesIds = types?.map((type) => type.id);

  const heighestUnitblock =
    regions
      ?.map((unit) => unit?.units?.length)
      .filter(Boolean)
      .sort((a, b) => a - b)
      .at(-1) ?? 0;

  return (
    <Formik
      initialValues={{
        ...initialValuesForm,
      }}
      onSubmit={onSubmit}
      validationSchema={formSchema}
      enableReinitialize
    >
      {({
        values,
        handleChange,
        setFieldError,
        setFieldTouched,
        setFieldValue,
        errors,
        touched,
      }) => {
        const appointmentErrors = errors.appointmentType as FormikErrors<{
          appointment: string;
        }>[];
        const appointmentTouched = touched.appointmentType as {
          appointment?: boolean;
        }[];

        const integration_codes_error =
          errors.integration_codes as FormikErrors<{
            code: string;
          }>[];
        const integration_codes_touched = touched.integration_codes as {
          code?: boolean;
        }[];

        const handleSelectAllUnits = () => {
          const units = selectAllUnits(values.units);
          setFieldValue("units", units);
        };
        const handleSelectRegion = (regionId: number) => {
          const units = selectRegion(regionId, values.units);
          setFieldValue("units", units);
        };
        const handleSelectUnit = (unitId: number) => {
          const units = selectUnit(unitId, values.units);
          setFieldValue("units", units);
        };

        const addAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length >= 1) {
            const file = e.target.files[0];
            setFieldValue("attach", file);
          }
        };

        let attachDisplayName: string | null = null;
        if (values.attach instanceof File) {
          attachDisplayName = values.attach.name;
        } else if (typeof values.attach === "string") {
          attachDisplayName = values.attach.split("/").pop() || values.attach;
        }

        return (
          <>
            <PanelContent title="Informações Básicas" loading={false}>
              <PartnerFormStyle id="partner-form">
                <div className="row">
                  <div className="md-4">
                    <Field
                      label="Parceiro"
                      id="name"
                      type="text"
                      value={values.name}
                      component={Input}
                      onChange={handleChange}
                      placeholder="Nome do parceiro"
                      error={touched.name && errors.name && errors.name}
                    />
                  </div>
                  <div className="md-4">
                    <Field
                      label="Auth ID"
                      id="auth_client_id"
                      type="text"
                      value={values.auth_client_id}
                      component={Input}
                      onChange={handleChange}
                      placeholder="Auth ID"
                      error={
                        touched.auth_client_id &&
                        errors.auth_client_id &&
                        errors.auth_client_id
                      }
                    />
                  </div>
                  <div className="md-4">
                    <Field
                      label="Login"
                      id="login"
                      type="text"
                      value={values.login}
                      component={Input}
                      onChange={handleChange}
                      placeholder="Login"
                      error={touched.login && errors.login && errors.login}
                    />
                  </div>
                  <FieldArray
                    name="appointmentType"
                    render={({ push, remove }) => (
                      <div className="appointment-types">
                        <div className="appointments-list ">
                          {values.appointmentType.map(
                            (appointment: any, index: number) => (
                              <div key={`appointmentType.${index}`}>
                                <Box display="flex">
                                  <Field
                                    label="Appointment Type"
                                    id={`appointmentType.${index}.appointment`}
                                    type="text"
                                    value={appointment.appointment}
                                    component={Input}
                                    onChange={handleChange}
                                    placeholder="Appointment Type"
                                    error={
                                      appointmentTouched?.[index]
                                        ?.appointment &&
                                      appointmentErrors?.[index]?.appointment
                                    }
                                  />
                                  <Field
                                    label="Nome Appointment Type"
                                    id={`appointmentType.${index}.name`}
                                    type="text"
                                    value={appointment.name}
                                    component={Input}
                                    onChange={handleChange}
                                    placeholder="Nome Appointment Type"
                                    error={
                                      appointmentTouched?.[index]
                                        ?.appointment &&
                                      appointmentErrors?.[index]?.appointment
                                    }
                                  />
                                </Box>

                                <Button
                                  textButton
                                  padding="0"
                                  onClick={() => remove(index)}
                                >
                                  {" "}
                                  <img src={TrashIcon} />
                                </Button>
                              </div>
                            )
                          )}
                        </div>

                        <Button
                          textButton
                          type="button"
                          onClick={() => {
                            const lastPartnerAdded =
                              values.appointmentType.at(-1);
                            const lastIndex = values.appointmentType.length - 1;

                            setFieldError(
                              `appointmentType.${lastIndex}.appointment`,
                              " "
                            );
                            setFieldTouched(
                              `appointmentType.${lastIndex}.appointment`,
                              true,
                              false
                            );

                            if (lastPartnerAdded?.appointment) {
                              push({ appointment: "", name: "" });
                            }
                          }}
                          padding="0"
                        >
                          + Adicionar novo appointment type
                        </Button>
                      </div>
                    )}
                  />
                  <FieldArray
                    name="integration_codes"
                    render={({ push, remove }) => (
                      <div className="appointment-types lg-12">
                        <div className="appointments-list ">
                          {values.integration_codes.map(
                            (code: any, index: number) => (
                              <div key={`integration_codes.${index}`}>
                                <Box display="flex">
                                  <Field
                                    label="Código integração"
                                    id={`integration_codes.${index}.id`}
                                    type="text"
                                    value={code.id}
                                    component={Input}
                                    onChange={handleChange}
                                    placeholder="Código integração"
                                    error={
                                      integration_codes_touched?.[index]
                                        ?.code &&
                                      integration_codes_error?.[index]?.code
                                    }
                                  />
                                  <Field
                                    label="Nome Código integração"
                                    id={`integration_codes.${index}.name`}
                                    type="text"
                                    value={code.name}
                                    component={Input}
                                    onChange={handleChange}
                                    placeholder="Nome Código integração"
                                    error={
                                      integration_codes_touched?.[index]
                                        ?.code &&
                                      integration_codes_error?.[index]?.code
                                    }
                                  />
                                </Box>

                                <Button
                                  textButton
                                  padding="0"
                                  onClick={() => remove(index)}
                                >
                                  {" "}
                                  <img src={TrashIcon} />
                                </Button>
                              </div>
                            )
                          )}
                        </div>

                        <Button
                          textButton
                          type="button"
                          onClick={() => {
                            const lastPartnerAdded =
                              values.appointmentType.at(-1);
                            const lastIndex = values.appointmentType.length - 1;

                            setFieldError(
                              `appointmentType.${lastIndex}.appointment`,
                              " "
                            );
                            setFieldTouched(
                              `appointmentType.${lastIndex}.appointment`,
                              true,
                              false
                            );

                            if (lastPartnerAdded?.appointment) {
                              push({ appointment: "", name: "" });
                            }
                          }}
                          padding="0"
                        >
                          + Adicionar novo código de integração
                        </Button>
                      </div>
                    )}
                  />

                  <div className="md-12 mt-20">
                    <Field
                      label="Necessário autorização ?"
                      id="product_needs_authorization"
                      component={SwitchInput}
                      value={values.product_needs_authorization}
                      onChange={() =>
                        setFieldValue(
                          "product_needs_authorization",
                          !values.product_needs_authorization
                        )
                      }
                      textOn="Habilitado"
                      textOff="Desabilitado"
                    />
                  </div>

                  <div className="md-12 mt-20">
                    <Field
                      label="Haverá cobrança domiciliar ?"
                      id="free_visit"
                      component={SwitchInput}
                      value={values.free_visit}
                      onChange={() =>
                        setFieldValue("free_visit", !values.free_visit)
                      }
                      textOn="Sim"
                      textOff="Não"
                    />
                  </div>

                  <div className="md-12 mt-20">
                    <Field
                      label="Segue fluxo de pré-orçamento ?"
                      id="pre_flow"
                      component={SwitchInput}
                      value={values.pre_flow}
                      onChange={() =>
                        setFieldValue("pre_flow", !values.pre_flow)
                      }
                      textOn="Sim"
                      textOff="Não"
                    />
                  </div>
                </div>
              </PartnerFormStyle>
            </PanelContent>

            <PanelContent title="Logo" loading={false}>
              <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
                <CampFile
                  className={touched.attach && errors.attach ? "invalid" : ""}
                >
                  UPLOAD DA LOGO
                  <img src={ImportDocument} alt="" />
                  <input
                    type="file"
                    id="fileInput"
                    name="fileInput"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      addAttachment(e)
                    }
                    style={{ display: "none" }}
                  />
                </CampFile>
              </label>
              {attachDisplayName && (
                <span
                  style={{
                    display: "block",
                    marginTop: 8,
                    fontSize: 14,
                    color: "#555",
                  }}
                >
                  {attachDisplayName}
                </span>
              )}
            </PanelContent>

            <PanelContent title="Vínculo de produtos" loading={false}>
              <Box display="flex" wrap="wrap" gap="0">
                <div className="md-12">
                  <Field
                    label="Categorias de produto"
                    id="categories"
                    type="text"
                    value={values.categories}
                    component={SelectSearch}
                    isMulti
                    options={types}
                    loading={isLoading}
                    readOnly={isLoading}
                    placeholder={
                      prod_categories && prod_categories?.length > 0
                        ? "Pesquisar categoria"
                        : "Sem categorias disponíveis"
                    }
                    valueKey="name"
                    labelKey="name"
                    onChange={(value: any) => {
                      setFieldValue("categories", value);
                    }}
                    description="Categorias de produtos que o parceiro poderá ofertar"
                    error={touched.categories && errors.categories}
                  />
                </div>

                <ProductsListStyles>
                  <Field
                    component={ExamsListIncluded}
                    idKey="id"
                    cssClass="md-12"
                    displayCodeKey="sku"
                    displayNameKey="product"
                    label="Produtos permitidos"
                    placeholder="Pesquisar produtos..."
                    fieldName="allowed_products"
                    list={values.prod_include}
                    clearOptions={() => {
                      setFieldValue("prod_include", []);
                    }}
                    manageListExam={(value: any) =>
                      setFieldValue("prod_include", value)
                    }
                    description="Produtos que o parceiro poderá ofertar."
                    productType={avaliableCategoriesIds}
                    error={touched.prod_include && errors.prod_include}
                  />

                  <Field
                    component={ExamsListIncluded}
                    displayCodeKey="sku"
                    displayNameKey="product"
                    idKey="id"
                    label="Produtos não permitidos"
                    placeholder="Pesquisar produtos..."
                    fieldName="not_allowed_products"
                    list={values.prod_exclude}
                    clearOptions={() => {
                      setFieldValue("prod_exclude", []);
                    }}
                    manageListExam={(value: any) =>
                      setFieldValue("prod_exclude", value)
                    }
                    productType={avaliableCategoriesIds}
                    description="Produtos que o parceiro não poderá ofertar."
                    error={touched.prod_exclude && errors.prod_exclude}
                  />
                </ProductsListStyles>
              </Box>
            </PanelContent>

            <PanelContent title="Vínculo de unidades" loading={false}>
              <Box display="flex" wrap="wrap" gap="0">
                <CheckboxList
                  error={
                    touched.units?.include && errors?.units?.include
                      ? errors.units.include
                      : ""
                  }
                  title="Unidades*"
                >
                  <Box mt="15">
                    <Field
                      component={CustomCheckbox}
                      type="checkbox"
                      label="Todas unidades"
                      name="units"
                      onChange={handleSelectAllUnits}
                      checked={values.units.include.includes(0)}
                    />
                    <CheckboxSublist
                      className="checkbox-units"
                      height={heighestUnitblock * 31}
                    >
                      {regions?.map((region) => (
                        <SubListCheckbox
                          key={region.id}
                          region={region}
                          handleSelectAllRegionUnits={handleSelectRegion}
                          handleSelectUnits={handleSelectUnit}
                        />
                      ))}
                    </CheckboxSublist>
                  </Box>
                </CheckboxList>
              </Box>
            </PanelContent>
          </>
        );
      }}
    </Formik>
  );
};
